import { ThemeProvider } from '@globalmed/atlas-ui';
import dayjs from 'dayjs';
import i18next from 'i18next';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Outlet } from 'react-router-dom';
import { useListStore } from '@/stores/list';
import { useUsersStore } from '@/stores/users';
import { useWhitelabelStore } from '@/stores/whitelabel';
import { ELanguage, TNullable } from '@/types';
import { languageOptions } from '@/utils/constants';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import 'dayjs/locale/pt';
import { isValueOfEnum } from '@/utils/isValueOfEnum';

type TLanguageProviderProps = {
  language: ELanguage;
  setLanguage?: (value: ELanguage) => Promise<void>;
};

const LanguageContext = createContext<TNullable<TLanguageProviderProps>>({
  language: ELanguage.EN,
  setLanguage: () => new Promise((r) => r()),
});

/**
 * Provides application-wide language context using React Context API.
 * Handles loading the saved language, applying it to i18next and dayjs,
 * and updating user preferences and timezones.
 * Defaults to English if no valid language is found.
 * Persists selected language in localStorage (`i18nextLng`).
 * Automatically updates dayjs locale.
 * Updates user preferences and timezones if user is logged in.
 * @returns JSX.Element wrapping child components with LanguageContext provider
 */
export const LanguageProvider = () => {
  const [language, setLanguage] = useState<ELanguage>(ELanguage.EN);

  const { userData, changeApplicationLanguage } = useUsersStore((state) => ({
    userData: state.userData,
    changeApplicationLanguage: state.changeApplicationLanguage,
  }));

  const currentWhiteLabel = useWhitelabelStore(
    (state) => state.currentWhitelabel,
  );

  const getTimezones = useListStore((state) => state.getTimezones);

  const languageSetter = useCallback(
    async (languageValue: ELanguage) => {
      if (
        i18next.language === languageValue &&
        languageValue === userData?.language
      )
        return;

      try {
        await i18next.changeLanguage(languageValue);
        dayjs.locale(languageValue);
        localStorage.setItem('i18nextLng', languageValue);
        setLanguage(languageValue);

        if (userData?.language) {
          await changeApplicationLanguage(languageValue);
          await getTimezones();
        }
      } catch (error) {
        console.error('Language update failed:', error);
      }
    },
    [changeApplicationLanguage, getTimezones, userData?.language],
  );

  useEffect(() => {
    const languageUpdater = async () => {
      const savedLanguage = localStorage.getItem('i18nextLng');

      let languageValue = ELanguage.EN;

      if (userData?.language && isValueOfEnum(userData.language, ELanguage)) {
        languageValue = userData.language;
      } else if (savedLanguage && isValueOfEnum(savedLanguage, ELanguage)) {
        languageValue = savedLanguage;
      } else if (currentWhiteLabel?.whitelabel?.enabled) {
        languageValue = currentWhiteLabel.language;
      }

      if (!languageOptions.some((opt) => opt.id === languageValue)) {
        languageValue = ELanguage.EN;
      }

      await languageSetter(languageValue);
    };

    void languageUpdater();
  }, [userData?.language, languageSetter, currentWhiteLabel]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage: (lang: ELanguage) => languageSetter(lang),
      }}
    >
      <ThemeProvider>
        <Outlet />
      </ThemeProvider>
    </LanguageContext.Provider>
  );
};

/**
 * Custom hook to access the LanguageContext.
 * Provides the current language and a setter function to change it.
 * Must be used within a `LanguageProvider`.
 * Will throw an error if used outside of a `LanguageProvider`.
 * @returns An object containing:
 *  - `language`: The current language (ELanguage).
 *  - `setLanguage`: Function to update the language.
 */
export const useLanguageContext = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error(
      'No context. Be sure that component contains inside LanguageProvider component',
    );
  }
  return context;
};
